<template>
  <div class="card border-0 shadow-none rounded-1 mb-3">
    <div class="card-body p-3 p-xl-4">

      <!-- Buscador moderno -->
      <div class="search-bar mb-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div><h5 class="mb-0 fs-16">Productos</h5><small class="text-muted">Consulta, crea y modifica el catálogo</small></div>
          <div class="d-flex gap-2 flex-wrap justify-content-end">
            <button class="btn btn-primary btn-sm px-3" @click="abrirEditor()">+ Nuevo producto</button>
            <button class="btn btn-outline-primary btn-sm px-3" @click="loteEditorOpen=true">+ Nuevos en bloque</button>
            <button class="btn btn-outline-secondary btn-sm px-3" @click="abrirUbicacion()">Ubicación en bloque</button>
          </div>
        </div>
        <div class="d-flex gap-2 align-items-stretch flex-wrap">

          <!-- Código proveedor (primero y más importante) -->
          <div class="position-relative">
            <label class="fs-11 text-muted d-block mb-1 ps-1">Cód. Proveedor</label>
            <div class="search-input-wrap position-relative">
              <img src="../../../assets/img/icons/search.svg" alt="" class="search-icon" />
              <input
                ref="codigoInput"
                v-model="filtros.codigo"
                @input="onSearch"
                @keydown.escape="limpiarBusqueda"
                type="text"
                class="form-control search-input"
                placeholder="Ej: DM0829-001"
                style="min-width:160px;max-width:200px"
                autocomplete="off"
              />
              <button v-if="filtros.codigo" class="search-clear" @click="filtros.codigo=''; recargar()" title="Limpiar">×</button>
            </div>
          </div>

          <!-- Nombre / barcode -->
          <div class="flex-grow-1 position-relative">
            <label class="fs-11 text-muted d-block mb-1 ps-1">Nombre / Barcode</label>
            <div class="search-input-wrap position-relative">
              <img src="../../../assets/img/icons/search.svg" alt="" class="search-icon" />
              <input
                ref="searchInput"
                v-model="filtros.q"
                @input="onSearch"
                @keydown.escape="limpiarBusqueda"
                type="text"
                class="form-control search-input"
                placeholder="Buscar por nombre o código de barras..."
                autocomplete="off"
              />
              <button v-if="filtros.q" class="search-clear" @click="limpiarBusqueda" title="Limpiar">×</button>
            </div>
          </div>

          <!-- Talla -->
          <select class="form-select filter-select" v-model="filtros.idtalla" @change="recargar" title="Filtrar por talla">
            <option value="">Todas las tallas</option>
            <option v-for="t in tallas" :key="t.idtalla" :value="t.idtalla">{{ t.nombre }}</option>
          </select>

          <!-- Ordenar (solo en vista lista) -->
          <div v-if="vistaModo === 'lista'" class="sort-wrap d-flex gap-1">
            <select class="form-select filter-select" v-model="sortBy" @change="recargar" title="Ordenar por">
              <option value="stock">Mayor stock</option>
              <option value="nombre">A → Z</option>
              <option value="precvta">Precio</option>
              <option value="linea">Línea</option>
            </select>
            <button class="btn sort-dir-btn" @click="toggleSortDir" :title="sortDir === 'asc' ? 'Ascendente' : 'Descendente'">
              {{ sortDir === 'asc' ? '↑' : '↓' }}
            </button>
          </div>

          <!-- Solo con stock -->
          <label class="stock-toggle" :class="{ active: filtros.soloConStock }" title="Mostrar solo productos con stock disponible">
            <input type="checkbox" v-model="filtros.soloConStock" @change="recargar" hidden />
            <span class="stock-toggle-icon">📦</span>
            Con stock
          </label>

          <!-- Vista toggle -->
          <div class="vista-toggle">
            <button class="vista-btn" :class="{ active: vistaModo === 'lista' }" @click="setVista('lista')" title="Vista lista">
              ☰ Lista
            </button>
            <button class="vista-btn" :class="{ active: vistaModo === 'lineas' }" @click="setVista('lineas')" title="Vista por línea">
              🗂 Por línea
            </button>
          </div>
        </div>

        <!-- Chips de filtros activos -->
        <div v-if="hayFiltrosActivos" class="d-flex flex-wrap gap-1 mt-2 align-items-center">
          <span class="fs-12 text-muted me-1">Filtros:</span>
          <span v-if="filtros.q" class="filter-chip">
            "{{ filtros.q }}" <button @click="filtros.q = ''; recargar()">×</button>
          </span>
          <span v-if="filtros.idtalla !== ''" class="filter-chip">
            {{ nombreTalla(filtros.idtalla) }} <button @click="filtros.idtalla = ''; recargar()">×</button>
          </span>
          <span v-if="filtros.soloConStock" class="filter-chip">
            Con stock <button @click="filtros.soloConStock = false; recargar()">×</button>
          </span>
          <button class="btn fs-12 text-muted p-0 ms-1" @click="limpiarTodo">Limpiar todo</button>
        </div>
      </div>

      <!-- ══════════════════ VISTA LISTA ══════════════════ -->
      <template v-if="vistaModo === 'lista'">
        <!-- Contador -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fs-13 text-muted">
            <template v-if="!loading">
              <strong class="text-paragraph">{{ pagination.total.toLocaleString() }}</strong> productos
              <span v-if="hayFiltrosActivos"> encontrados</span>
            </template>
            <span v-else class="placeholder-glow"><span class="placeholder col-3 rounded"></span></span>
          </span>
          <span class="fs-12 text-muted">Pág. {{ currentPage }} de {{ totalPages }}</span>
        </div>

        <!-- Tabla -->
        <div class="table-responsive">
          <table class="table align-middle mb-0 productos-table">
            <thead>
              <tr>
                <th class="col-producto" @click="setSort('nombre')" :class="{ 'sort-active': sortBy === 'nombre' }">
                  PRODUCTO <span class="sort-arrow">{{ sortBy === 'nombre' ? (sortDir === 'asc' ? '↑' : '↓') : '' }}</span>
                </th>
                <th>GÉNERO / TALLA</th>
                <th>CÓDIGO</th>
                <th class="text-end cursor-sort" @click="setSort('precvta')" :class="{ 'sort-active': sortBy === 'precvta' }">
                  P.VENTA <span class="sort-arrow">{{ sortBy === 'precvta' ? (sortDir === 'asc' ? '↑' : '↓') : '' }}</span>
                </th>
                <th class="text-center"><span class="th-badge th-t1">939</span></th>
                <th class="text-center"><span class="th-badge th-t2">1179</span></th>
                <th class="text-center cursor-sort" @click="setSort('stock')" :class="{ 'sort-active': sortBy === 'stock' }">
                  TOTAL <span class="sort-arrow">{{ sortBy === 'stock' ? (sortDir === 'asc' ? '↑' : '↓') : '' }}</span>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in pageSize" :key="n">
                  <td colspan="8" class="py-1">
                    <div class="placeholder-glow"><span class="placeholder col-12 rounded" style="height:16px"></span></div>
                  </td>
                </tr>
              </template>
              <tr v-else-if="!productos.length && hayFiltrosActivos">
                <td colspan="8" class="text-center text-muted py-5 fs-14">
                  <img src="../../../assets/img/icons/search.svg" alt="" style="opacity:.2;width:40px" class="d-block mx-auto mb-2" />
                  No se encontraron productos.
                </td>
              </tr>
              <tr v-else-if="!productos.length"><td colspan="8" class="py-4"></td></tr>
              <template v-else v-for="grupo in productosAgrupados" :key="grupo.key">
                <!-- Fila resumen del grupo -->
                <tr class="producto-row grupo-row"
                    :class="{ 'grupo-expandido': expandidos[grupo.key] }"
                    @click="toggleGrupo(grupo.key)">
                  <td class="ps-0 col-producto">
                    <div class="d-flex align-items-center gap-2">
                      <span class="grupo-chevron" :class="{ 'grupo-chevron--open': expandidos[grupo.key] }">›</span>
                      <div>
                        <div class="fs-13 fw-semibold text-paragraph lh-sm">{{ grupo.nombre }}</div>
                        <div v-if="grupo.variantes.length > 1" class="fs-11 text-muted mt-1">
                          {{ grupo.variantes.length }} variantes
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="genero-talla-cell">
                      <span v-if="grupo.talla && grupo.talla !== 'Sin Especificar'" class="talla-badge" :class="tallaCls(grupo.talla)">{{ tallaAbrev(grupo.talla) }}</span>
                      <span v-if="grupo.variantes.length === 1 && tallaReal(grupo.variantes[0])" class="talla-num">{{ tallaReal(grupo.variantes[0]) }}</span>
                      <span v-else-if="grupo.variantes.length > 1 && tallaReal(grupo.variantes[0])" class="talla-rango">
                        {{ grupo.variantes.map(v => tallaReal(v)).filter(Boolean).join(' · ') }}
                      </span>
                    </div>
                  </td>
                  <td class="fs-12 font-monospace text-muted">
                    {{ grupo.codigoProv || '—' }}
                  </td>
                  <td class="text-end fs-13 fw-semibold text-paragraph">S/ {{ Number(grupo.precvta).toFixed(2) }}</td>
                  <td class="text-center"><span class="stock-pill" :class="stockCls(grupo.stock_t1)">{{ fmtN(grupo.stock_t1) }}</span></td>
                  <td class="text-center"><span class="stock-pill" :class="stockCls(grupo.stock_t2)">{{ fmtN(grupo.stock_t2) }}</span></td>
                  <td class="text-center"><span class="stock-pill fw-bold" :class="stockCls(grupo.stock_total, grupo.variantes[0]?.stockminimo)">{{ fmtN(grupo.stock_total) }}</span></td>
                  <td class="pe-0 text-end" @click.stop>
                    <div class="d-flex justify-content-end gap-1">
                      <a v-if="grupo.variantes.length === 1"
                         href="javascript:void(0)" @click.stop="verDetalle(grupo.variantes[0])" data-bs-toggle="modal" data-bs-target="#productoModal" title="Ver detalle">
                        <img src="../../../assets/img/icons/eye.svg" alt="Ver" />
                      </a>
                      <button v-if="grupo.variantes.length === 1" class="icon-action" @click="abrirEditor(grupo.variantes[0])" title="Modificar producto">✎</button>
                      <button v-if="grupo.codigoProv" class="icon-action" @click="abrirEditorGrupo(grupo)" title="Modificar todas las tallas">▦</button>
                      <button v-if="grupo.codigoProv" class="icon-action location-action" @click="abrirUbicacion(grupo)" title="Ver o cambiar ubicación / andamio">⌖</button>
                      <a href="javascript:void(0)" @click.stop="verKardex(grupo.variantes[0])" title="Ver movimientos / Kardex" class="ms-1">
                        <img src="../../../assets/img/icons/item-list.svg" alt="Kardex" style="width:16px;opacity:.7" />
                      </a>
                    </div>
                  </td>
                </tr>
                <!-- Filas detalle de variantes (expandidas) -->
                <template v-if="expandidos[grupo.key] && grupo.variantes.length > 1">
                  <tr v-for="p in grupo.variantes" :key="p.idprod" class="variante-row">
                    <td class="col-producto" style="padding-left:2.4rem!important">
                      <div class="d-flex align-items-center gap-2">
                        <span class="var-dot"></span>
                        <div>
                          <div class="fs-12 font-monospace text-paragraph fw-semibold">{{ p.codbarras || '—' }}</div>
                          <div class="fs-11 text-muted">EAN / Barcode</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="genero-talla-cell">
                        <span v-if="p.talla && p.talla !== 'Sin Especificar'" class="talla-badge" :class="tallaCls(p.talla)">{{ tallaAbrev(p.talla) }}</span>
                        <span v-if="tallaReal(p)" class="talla-num">{{ tallaReal(p) }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="fs-12 font-monospace text-paragraph fw-semibold">{{ p.codigo_prov || '—' }}</div>
                      <div class="fs-11 text-muted">Cód. proveedor</div>
                    </td>
                    <td></td>
                    <td class="text-center"><span class="stock-pill" :class="stockCls(p.stock_t1)">{{ fmtN(p.stock_t1) }}</span></td>
                    <td class="text-center"><span class="stock-pill" :class="stockCls(p.stock_t2)">{{ fmtN(p.stock_t2) }}</span></td>
                    <td class="text-center"><span class="stock-pill" :class="stockCls(p.stock_total)">{{ fmtN(p.stock_total) }}</span></td>
                    <td class="pe-0 text-end" @click.stop>
                      <a href="javascript:void(0)" @click.stop="verDetalle(p)" data-bs-toggle="modal" data-bs-target="#productoModal" title="Ver detalle">
                        <img src="../../../assets/img/icons/eye.svg" alt="Ver" />
                      </a>
                      <button class="icon-action" @click="abrirEditor(p)" title="Modificar producto">✎</button>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ══════════════════ VISTA POR LÍNEA ══════════════════ -->
      <template v-else>
        <div v-if="lineasLoading" class="text-center py-5">
          <div class="spinner-border text-purple" role="status"></div>
          <div class="fs-13 text-muted mt-2">Cargando líneas...</div>
        </div>
        <div v-else-if="!lineas.length" class="text-center text-muted py-5 fs-14">
          No hay líneas disponibles.
        </div>
        <div v-else class="lineas-accordion">
          <div v-for="linea in lineas" :key="linea.idlinea" class="linea-grupo">
            <div class="linea-header" @click="toggleLinea(linea.idlinea)">
              <span class="linea-chevron">{{ lineasExpandidas[linea.idlinea] ? '▾' : '▸' }}</span>
              <span class="linea-nombre">{{ linea.nombre }}</span>
              <span class="linea-count">{{ linea.total.toLocaleString() }} prod.</span>
            </div>

            <div v-if="lineasExpandidas[linea.idlinea]" class="linea-body">
              <div v-if="lineasCargando[linea.idlinea]" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-purple"></div>
              </div>
              <div v-else-if="!lineasProductos[linea.idlinea]?.length" class="text-center text-muted py-3 fs-13">
                Sin productos con los filtros actuales.
              </div>
              <template v-else>
                <table class="table table-sm align-middle mb-0 productos-table fs-13">
                  <tbody>
                    <tr v-for="p in lineasProductos[linea.idlinea]" :key="p.idprod"
                        class="producto-row" @click="verDetalle(p)" data-bs-toggle="modal" data-bs-target="#productoModal" style="cursor:pointer">
                      <td class="ps-2 col-producto">
                        <div class="fw-semibold text-paragraph lh-sm">{{ p.nombre || '—' }}</div>
                        <div v-if="p.codbarras" class="fs-11 font-monospace text-muted">{{ p.codbarras }}</div>
                      </td>
                      <td>
                        <div class="genero-talla-cell">
                          <span v-if="p.talla && p.talla !== 'Sin Especificar'" class="talla-badge" :class="tallaCls(p.talla)">{{ tallaAbrev(p.talla) }}</span>
                          <span v-if="tallaReal(p)" class="talla-num">{{ tallaReal(p) }}</span>
                        </div>
                      </td>
                      <td class="text-end fw-semibold">S/ {{ Number(p.precvta).toFixed(2) }}</td>
                      <td class="text-center"><span class="stock-pill" :class="stockCls(p.stock_t1)">{{ fmtN(p.stock_t1) }}</span></td>
                      <td class="text-center"><span class="stock-pill" :class="stockCls(p.stock_t2)">{{ fmtN(p.stock_t2) }}</span></td>
                      <td class="text-center"><span class="stock-pill fw-bold" :class="stockCls(p.stock_total, p.stockminimo)">{{ fmtN(p.stock_total) }}</span></td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="lineasTiene_mas[linea.idlinea]" class="text-center py-2 border-top">
                  <button class="btn btn-sm style-six fs-12 px-3"
                    @click.stop="cargarMasLinea(linea.idlinea)"
                    :disabled="lineasCargando[linea.idlinea]">
                    Ver más ({{ lineasOffset[linea.idlinea] }} de {{ linea.total }})
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Paginación (solo vista lista) -->
  <div v-if="vistaModo === 'lista' && pagination.total > 0" class="row pb-4 align-items-center">
    <div class="col-sm-6">
      <div class="d-flex align-items-center gap-2 justify-content-center justify-content-sm-start page-unit">
        <span class="fs-13">Por página</span>
        <select class="text-title border-0 fs-14 bg-transparent" v-model="pageSize" @change="onPageSizeChange">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>
    <div class="col-sm-6 text-sm-end text-center">
      <ul class="page-nav list-style">
        <li><a href="#" @click.prevent="prevPage"><img src="../../../assets/img/icons/left-arrow-purple.svg" alt="" /></a></li>
        <li v-for="pg in visiblePages" :key="pg">
          <a href="#" @click.prevent="goToPage(pg)" :class="{ active: pg === currentPage }">{{ pg }}</a>
        </li>
        <li><a href="#" @click.prevent="nextPage"><img src="../../../assets/img/icons/right-arrow-purple.svg" alt="" /></a></li>
      </ul>
    </div>
  </div>

  <ProductoModal :producto="seleccionado" @edit="abrirEditor" />
  <ProductoEditorModal :open="editorOpen" :product-id="editorId" @close="editorOpen=false" @saved="productoGuardado" />
  <ProductoGrupoEditorModal :open="grupoEditorOpen" :codigo-proveedor="grupoEditorCodigo" @close="grupoEditorOpen=false" @saved="grupoGuardado" />
  <ProductoLoteEditorModal :open="loteEditorOpen" @close="loteEditorOpen=false" @saved="loteGuardado" />
  <ProductoUbicacionLoteModal :open="ubicacionOpen" :codigo-inicial="ubicacionCodigo" @close="ubicacionOpen=false" @saved="ubicacionGuardada" />

  <!-- Modal Kardex -->
  <div v-if="kardexModal" class="modal fade show d-block" style="background:rgba(0,0,0,.5);z-index:1060" @click.self="kardexModal=null">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header py-2">
          <div>
            <h6 class="modal-title fw-bold mb-0">Movimientos — {{ kardexModal.nombre }}</h6>
            <small class="text-muted">{{ kardexModal.codigo_prov || kardexModal.codbarras || '' }}</small>
          </div>
          <button class="btn-close btn-sm" @click="kardexModal=null"></button>
        </div>
        <div class="modal-body py-2">
          <div v-if="kardexCargando" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary me-2"></div>Cargando movimientos…
          </div>
          <div v-else-if="!kardexRows.length" class="text-center py-4 text-muted">
            No hay movimientos registrados para este producto.
          </div>
          <div v-else>
            <!-- Filtro por almacén -->
            <div class="d-flex gap-2 mb-2 align-items-center">
              <small class="text-muted">Almacén:</small>
              <select v-model="kardexAlmacen" class="form-select form-select-sm" style="width:auto">
                <option value="">Todos</option>
                <option value="1">939</option>
                <option value="15">1179</option>
              </select>
              <small class="text-muted ms-auto">{{ kardexRowsFiltrados.length }} movimientos</small>
            </div>
            <div class="table-responsive" style="max-height:450px;overflow-y:auto">
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-light sticky-top">
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Documento</th>
                    <th>Detalle / Proveedor</th>
                    <th>Ruta del movimiento</th>
                    <th class="text-end text-success">Ingreso</th>
                    <th class="text-end text-danger">Salida</th>
                    <th class="text-end">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in kardexRowsFiltrados" :key="i"
                    :class="movInfo(row).clase === 'traslado' ? 'kardex-traslado' : (row.tipo === 0 ? 'table-success-subtle' : '')">
                    <td class="small">{{ fmtFecha(row.fecha) }}</td>
                    <td>
                      <span class="badge" :class="badgeMovimientoCls(row)">
                        {{ movInfo(row).icono }} {{ movInfo(row).etiqueta }}
                      </span>
                    </td>
                    <td class="font-monospace small">{{ row.detalle || '—' }}</td>
                    <td class="small">{{ row.detalle_extra || '—' }}</td>
                    <td class="small fw-semibold">{{ rutaKardex(row).texto }}</td>
                    <td class="text-end text-success fw-semibold small">{{ row.tipo === 0 ? fmtN(row.ing_cantidad) : '' }}</td>
                    <td class="text-end text-danger fw-semibold small">{{ row.tipo === 1 ? fmtN(row.sal_cantidad) : '' }}</td>
                    <td class="text-end fw-bold small">{{ fmtN(row.saldo_cantidad) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../../../services/api';
import ProductoModal from './ProductoModal.vue';
import ProductoEditorModal from './ProductoEditorModal.vue';
import ProductoGrupoEditorModal from './ProductoGrupoEditorModal.vue';
import ProductoLoteEditorModal from './ProductoLoteEditorModal.vue';
import ProductoUbicacionLoteModal from './ProductoUbicacionLoteModal.vue';
import { clasificarMovimientoKardex, rutaMovimientoKardex } from '../../../utils/kardexMovimiento';

const TALLAS = [
  { idtalla: 0, nombre: 'Infante' },
  { idtalla: 1, nombre: 'Niño' },
  { idtalla: 5, nombre: 'Niña' },
  { idtalla: 2, nombre: 'Dama' },
  { idtalla: 3, nombre: 'Caballero' },
  { idtalla: 4, nombre: 'Unisex (adultos)' },
  { idtalla: 6, nombre: 'Unisex (niños)' },
  { idtalla: -1, nombre: 'Sin Especificar' },
];

const LINEA_PAGE = 30;

export default {
  name: 'ProductsList',
  components: { ProductoModal, ProductoEditorModal, ProductoGrupoEditorModal, ProductoLoteEditorModal, ProductoUbicacionLoteModal },
  data() {
    return {
      // Vista lista
      productos: [],
      pagination: { total: 0 },
      loading: false,
      currentPage: 1,
      pageSize: 50,
      // Vista por línea
      vistaModo: 'lista',
      lineas: [],
      lineasLoading: false,
      lineasExpandidas: {},
      lineasProductos: {},
      lineasCargando: {},
      lineasOffset: {},
      lineasTiene_mas: {},
      // Común
      searchTimer: null,
      seleccionado: null,
      editorOpen: false,
      editorId: null,
      grupoEditorOpen: false,
      grupoEditorCodigo: '',
      loteEditorOpen: false,
      ubicacionOpen: false,
      ubicacionCodigo: '',
      tallas: TALLAS,
      sortBy: 'stock',
      sortDir: 'desc',
      filtros: { q: '', codigo: '', idtalla: '', soloConStock: false },
      expandidos: {},
      // Kardex
      kardexModal: null,
      kardexRows: [],
      kardexCargando: false,
      kardexAlmacen: '',
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.pagination.total / this.pageSize) || 1; },
    kardexRowsFiltrados() {
      if (!this.kardexAlmacen) return this.kardexRows;
      return this.kardexRows.filter(r => r.almacenid === parseInt(this.kardexAlmacen));
    },
    productosAgrupados() {
      const map = new Map();
      for (const p of this.productos) {
        const codigoGrupo = String(p.codigo_prov || '').trim();
        const key = codigoGrupo || `SIN-CODIGO-${p.idprod}`;
        if (!map.has(key)) {
          map.set(key, {
            key, nombre: p.nombre, talla: p.talla, precvta: p.precvta,
            codigoProv: p.codigo_prov || null,
            stock_t1: 0, stock_t2: 0, stock_total: 0, variantes: [],
          });
        }
        const g = map.get(key);
        g.variantes.push(p);
        if (!g.codigoProv && p.codigo_prov) g.codigoProv = p.codigo_prov;
        g.stock_t1    = (parseFloat(g.stock_t1)    || 0) + (parseFloat(p.stock_t1)    || 0);
        g.stock_t2    = (parseFloat(g.stock_t2)    || 0) + (parseFloat(p.stock_t2)    || 0);
        g.stock_total = (parseFloat(g.stock_total) || 0) + (parseFloat(p.stock_total) || 0);
      }
      const tallaValor = (v) => {
        const texto = String(this.tallaReal(v) || '').replace(',', '.');
        const numero = Number.parseFloat(texto.replace(/[^0-9.]/g, ''));
        return Number.isFinite(numero) ? numero : Number.POSITIVE_INFINITY;
      };
      return Array.from(map.values()).map((grupo) => ({
        ...grupo,
        variantes: grupo.variantes.sort((a, b) =>
          tallaValor(a) - tallaValor(b) ||
          String(this.tallaReal(a) || '').localeCompare(String(this.tallaReal(b) || ''), 'es', { numeric: true })
        ),
      }));
    },
    visiblePages() {
      const s = Math.max(1, this.currentPage - 2), e = Math.min(this.totalPages, this.currentPage + 2);
      const r = []; for (let i = s; i <= e; i++) r.push(i); return r;
    },
    hayFiltrosActivos() {
      return this.filtros.q || this.filtros.idtalla !== '' || this.filtros.soloConStock;
    },
  },
  mounted() { this.cargar(); },
  methods: {
    movInfo(movimiento) {
      return clasificarMovimientoKardex(movimiento);
    },
    rutaKardex(movimiento) {
      return rutaMovimientoKardex(movimiento, this.kardexRows);
    },
    badgeMovimientoCls(movimiento) {
      const clase = this.movInfo(movimiento).clase;
      if (clase === 'traslado') return 'bg-warning-subtle text-warning-emphasis';
      if (clase === 'entrada') return 'bg-success-subtle text-success';
      return 'bg-danger-subtle text-danger';
    },
    // ── Vista lista ──────────────────────────────────────────────────────────
    async cargar() {
      this.loading = true;
      try {
        const params = {
          limit: this.pageSize,
          page:  this.currentPage,
          publicado: false,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        };
        if (this.filtros.q)              params.q            = this.filtros.q;
        if (this.filtros.codigo)         params.codigo_prov  = this.filtros.codigo;
        if (this.filtros.idtalla !== '')  params.idtalla      = this.filtros.idtalla;
        if (this.filtros.soloConStock)    params.soloConStock = true;
        const res = await api.get('/productos', { params });
        this.productos  = res.data.data || [];
        this.pagination = res.data.pagination || { total: 0 };
        this.expandidos = {};
      } catch { this.productos = []; } finally { this.loading = false; }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.currentPage = 1; this.cargar(); }, 300);
    },
    recargar() {
      this.currentPage = 1;
      if (this.vistaModo === 'lista') {
        this.cargar();
      } else {
        this.lineasExpandidas = {};
        this.lineasProductos  = {};
        this.lineasOffset     = {};
        this.lineasTiene_mas  = {};
        this.cargarLineas();
      }
    },
    onPageSizeChange() { this.currentPage = 1; this.cargar(); },
    goToPage(p)  { if (p >= 1 && p <= this.totalPages) { this.currentPage = p; this.cargar(); } },
    prevPage()   { this.goToPage(this.currentPage - 1); },
    nextPage()   { this.goToPage(this.currentPage + 1); },
    setSort(campo) {
      if (this.sortBy === campo) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy  = campo;
        this.sortDir = campo === 'stock' ? 'desc' : 'asc';
      }
      this.recargar();
    },
    toggleSortDir() { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; this.recargar(); },
    limpiarBusqueda() { this.filtros.q = ''; this.recargar(); this.$refs.searchInput?.focus(); },
    limpiarTodo()     { this.filtros = { q: '', idtalla: '', soloConStock: false }; this.recargar(); },

    // ── Vista por línea ──────────────────────────────────────────────────────
    async setVista(modo) {
      this.vistaModo = modo;
      if (modo === 'lineas' && !this.lineas.length) {
        await this.cargarLineas();
      }
    },
    async cargarLineas() {
      this.lineasLoading = true;
      try {
        const res = await api.get('/productos/lineas', { params: { publicado: false } });
        this.lineas = res.data.data || [];
      } catch { this.lineas = []; } finally { this.lineasLoading = false; }
    },
    async toggleLinea(idlinea) {
      if (this.lineasExpandidas[idlinea]) {
        this.lineasExpandidas = { ...this.lineasExpandidas, [idlinea]: false };
        return;
      }
      this.lineasExpandidas = { ...this.lineasExpandidas, [idlinea]: true };
      if (!this.lineasProductos[idlinea]) {
        await this.cargarProductosLinea(idlinea, 0);
      }
    },
    async cargarProductosLinea(idlinea, offset) {
      this.lineasCargando = { ...this.lineasCargando, [idlinea]: true };
      try {
        const page = Math.floor(offset / LINEA_PAGE) + 1;
        const params = { publicado: false, idlinea, limit: LINEA_PAGE, page, sortBy: 'stock', sortDir: 'desc' };
        if (this.filtros.q)              params.q            = this.filtros.q;
        if (this.filtros.idtalla !== '')  params.idtalla      = this.filtros.idtalla;
        if (this.filtros.soloConStock)    params.soloConStock = true;
        const res = await api.get('/productos', { params });
        const nuevos = res.data.data || [];
        this.lineasProductos = {
          ...this.lineasProductos,
          [idlinea]: offset === 0 ? nuevos : [...(this.lineasProductos[idlinea] || []), ...nuevos],
        };
        this.lineasOffset    = { ...this.lineasOffset,    [idlinea]: (this.lineasProductos[idlinea] || []).length };
        this.lineasTiene_mas = { ...this.lineasTiene_mas, [idlinea]: nuevos.length === LINEA_PAGE };
      } catch (e) { console.error(e); } finally {
        this.lineasCargando = { ...this.lineasCargando, [idlinea]: false };
      }
    },
    async cargarMasLinea(idlinea) {
      await this.cargarProductosLinea(idlinea, this.lineasOffset[idlinea] || 0);
    },

    // ── Helpers ──────────────────────────────────────────────────────────────
    tallaAbrev(t) {
      const m = { 'Caballero':'Cab.', 'Dama':'Dam.', 'Niño':'Niño', 'Niña':'Niña',
                  'Infante':'Inf.', 'Unisex (adultos)':'Uni.', 'Unisex (niños)':'Uni.' };
      return m[t] || t;
    },
    tallaReal(p) {
      if (!p) return null;
      // Campo usa = talla US (zapatillas: 5, 5.5, 8...), campo peru = talla PE/EU
      if (p.usa && p.usa.trim())  return p.usa.trim();
      if (p.peru && p.peru.trim()) return p.peru.trim();
      // Fallback: extraer última palabra del nombre
      return this.extractTalla(p.nombre);
    },
    extractTalla(nombre) {
      const last = (nombre || '').trim().split(/\s+/).pop() || '';
      // Tallas de ropa
      const ropa = ['XS','S','M','L','XL','XXL','XXXL','2XL','3XL','4XL'];
      if (ropa.includes(last.toUpperCase())) return last.toUpperCase();
      // Tallas numéricas: US (1–15 incl. medios: 5.5, 8.5) y EU (16–50)
      if (/^\d{1,2}(\.\d)?$/.test(last)) {
        const n = parseFloat(last);
        if (n >= 1 && n <= 50) return last;
      }
      return null;
    },
    toggleGrupo(key) { this.expandidos = { ...this.expandidos, [key]: !this.expandidos[key] }; },
    async verKardex(producto) {
      if (!producto?.idprod) return;
      this.kardexModal = producto;
      this.kardexRows = [];
      this.kardexAlmacen = '';
      this.kardexCargando = true;
      try {
        const r = await api.get(`/productos/${producto.idprod}/kardex`, { params: { limit: 200 } });
        const raw = r.data?.data ?? r.data ?? [];
        // El endpoint devuelve tbreporte_kardex_valorizado_detalle
        // tipo=0 ingreso, tipo=1 salida
        this.kardexRows = (Array.isArray(raw) ? raw : (raw.rows ?? [])).map(row => {
          // backend devuelve tipo='E'/'S' o tipo=0/1
          const esIngreso = row.tipo === 'E' || row.tipo === 0 ||
                            (row.tipo === undefined && (row.ing_cantidad ?? row.cantidad) > 0);
          const cantidad = parseFloat(row.cantidad ?? row.ing_cantidad ?? row.sal_cantidad ?? 0);
          return {
            ...row,
            tipo: esIngreso ? 0 : 1,
            ing_cantidad: esIngreso ? cantidad : 0,
            sal_cantidad: esIngreso ? 0 : cantidad,
            saldo_cantidad: parseFloat(row.saldo_cantidad ?? row.saldo ?? 0),
          };
        });
      } catch(e) { console.error(e); this.kardexRows = []; }
      finally { this.kardexCargando = false; }
    },
    fmtFecha(f) {
      if (!f) return '—';
      const s = String(f).substring(0, 10); return new Date(s + 'T00:00:00').toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    verDetalle(p)    { this.seleccionado = p; },
    abrirEditor(p = null) {
      const id = Number(p?.idprod);
      this.editorId = Number.isInteger(id) && id > 0 ? id : null;
      if (p && !this.editorId) return;
      this.editorOpen = true;
    },
    abrirEditorGrupo(grupo) {
      this.grupoEditorCodigo = grupo?.codigoProv || '';
      if (this.grupoEditorCodigo) this.grupoEditorOpen = true;
    },
    abrirUbicacion(grupo = null) {
      this.ubicacionCodigo = String(
        grupo?.codigoProv || this.filtros.codigo ||
        (this.productosAgrupados.length === 1 ? this.productosAgrupados[0].codigoProv : '') || ''
      ).trim();
      this.ubicacionOpen = true;
    },
    productoGuardado() { this.editorOpen = false; this.cargar(); },
    grupoGuardado() { this.grupoEditorOpen = false; this.cargar(); },
    loteGuardado() { this.loteEditorOpen = false; this.cargar(); },
    ubicacionGuardada() { this.ubicacionOpen = false; this.cargar(); },
    nombreTalla(id)  { return TALLAS.find(t => t.idtalla == id)?.nombre || id; },
    fmtN(v)  { const n = parseFloat(v) || 0; return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1); },
    stockCls(v, min = 0) {
      const n = parseFloat(v) || 0;
      if (n === 0)         return 'sc-cero';
      if (n <= (min || 2)) return 'sc-bajo';
      return 'sc-ok';
    },
    tallaCls(t) {
      return { Caballero:'tc-c', Dama:'tc-d', Niño:'tc-n', Niña:'tc-n', Infante:'tc-n',
               'Unisex (adultos)':'tc-u', 'Unisex (niños)':'tc-u' }[t] || '';
    },
  },
};
</script>

<style scoped>
/* Buscador */
.search-input-wrap { min-width: 240px; }
.search-input { padding-left: 36px; padding-right: 36px; height: 38px; }
.search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:16px; opacity:.4; pointer-events:none; }
.search-clear { position:absolute; right:10px; top:50%; transform:translateY(-50%); background:none; border:none; font-size:18px; line-height:1; color:#aaa; cursor:pointer; padding:0; }
.search-clear:hover { color:#555; }
.filter-select { height: 38px; min-width: 130px; max-width: 170px; font-size: 13px; }
.sort-dir-btn { width:38px; height:38px; border:1px solid #dee2e6; background:#fff; font-size:16px; padding:0; }
.sort-dir-btn:hover { background:#f3f8ff; }
.stock-toggle { display:flex; align-items:center; gap:5px; height:38px; padding:0 12px; border:1px solid #dee2e6; border-radius:6px; font-size:13px; color:#666; cursor:pointer; user-select:none; white-space:nowrap; background:#fff; transition:all .15s; }
.stock-toggle.active { background:#f0fdf4; border-color:#86efac; color:#16a34a; }
.stock-toggle-icon { font-size:15px; }

/* Vista toggle */
.vista-toggle { display:flex; border:1px solid #dee2e6; border-radius:6px; overflow:hidden; }
.vista-btn { padding: 0 12px; height:38px; background:#fff; border:none; font-size:13px; color:#666; cursor:pointer; white-space:nowrap; transition:all .15s; }
.vista-btn + .vista-btn { border-left:1px solid #dee2e6; }
.vista-btn.active { background:#4f46e5; color:#fff; }
.vista-btn:not(.active):hover { background:#f3f4f6; }

/* Chips */
.filter-chip { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; background:#eff6ff; border-radius:20px; font-size:12px; color:#2563eb; }
.filter-chip button { background:none; border:none; padding:0; font-size:14px; line-height:1; color:#60a5fa; cursor:pointer; }

/* Tabla */
.productos-table th { border-bottom: 2px solid #f0f4f8; font-size:12px; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:.4px; cursor:default; white-space:nowrap; }
.productos-table th.cursor-sort, .productos-table th.col-producto { cursor:pointer; }
.productos-table th.sort-active { color:#4f46e5; }
.sort-arrow { font-size:12px; }
.col-producto { min-width: 200px; }
.producto-row td { border-bottom: 1px solid #f5f7fa; padding-top: 7px; padding-bottom: 7px; }
.producto-row:hover td { background: #f8fbff; }

/* Stock pills */
.stock-pill { display:inline-block; min-width:40px; text-align:center; padding:2px 9px; border-radius:20px; font-size:13px; }
.sc-cero { background:#fee2e2; color:#dc2626; }
.sc-bajo { background:#fef9c3; color:#92400e; }
.sc-ok   { background:#dcfce7; color:#15803d; }

/* Género / Talla */
.genero-talla-cell { display:flex; align-items:center; gap:6px; white-space:nowrap; }
.talla-badge { display:inline-block; padding:2px 6px; border-radius:4px; font-size:11px; font-weight:600; white-space:nowrap; }
.tc-c { background:#dbeafe; color:#1d4ed8; }
.tc-d { background:#fce7f3; color:#be185d; }
.tc-n { background:#fef3c7; color:#92400e; }
.tc-u { background:#ede9fe; color:#6d28d9; }
.talla-num { font-size:13px; font-weight:700; color:#1e293b; }
.talla-rango { font-size:11px; color:#6b7280; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* Cabeceras almacén */
.th-badge { display:inline-block; padding:2px 8px; border-radius:4px; font-size:11px; font-weight:600; }
.th-t1 { background:#eff6ff; color:#2563eb; }
.th-t2 { background:#f0fdf4; color:#16a34a; }

/* Agrupación de variantes */
.grupo-chevron {
  display: inline-block; width: 16px; text-align: center;
  font-size: 16px; color: #9ca3af; transition: transform .15s; line-height: 1;
  flex-shrink: 0; cursor: pointer;
}
.grupo-chevron--open { transform: rotate(90deg); color: #4f46e5; }
.grupo-row { cursor: pointer; }
.grupo-expandido td { background: #f8f7ff !important; }
.variante-row td { background: #fafafa; border-bottom: 1px solid #f0f0f0; }
.variante-row:last-child td { border-bottom: 2px solid #e0d9ff; }
.var-dot { width:6px; height:6px; border-radius:50%; background:#c4b5fd; flex-shrink:0; }

/* Acordeón por línea */
.lineas-accordion { border:1px solid #eef2f7; border-radius:8px; overflow:hidden; }
.linea-grupo + .linea-grupo { border-top:1px solid #eef2f7; }
.linea-header { display:flex; align-items:center; gap:10px; padding:10px 14px; cursor:pointer; user-select:none; background:#fafbfd; transition:background .1s; }
.linea-header:hover { background:#f0f4ff; }
.linea-chevron { font-size:13px; color:#4f46e5; width:14px; flex-shrink:0; }
.linea-nombre { flex:1; font-size:14px; font-weight:500; color:#1e293b; }
.linea-count { font-size:12px; color:#94a3b8; background:#f1f5f9; padding:1px 8px; border-radius:10px; }
.linea-body { background:#fff; border-top:1px solid #eef2f7; }
.linea-body .productos-table { border:none; }
.fs-11 { font-size: 11px; }
.kardex-traslado > td { background:#fffbeb; }
.icon-action{border:0;background:#eef2ff;color:#4338ca;width:30px;height:30px;border-radius:5px;font-size:16px;line-height:1}.icon-action:hover{background:#ddd6fe}.location-action{background:#fef3c7;color:#92400e}.location-action:hover{background:#fde68a}
</style>
